import { useTranslation } from 'react-i18next';

import { Tooltip } from '@mui/material';

import CursorToolbar from '../../assets/toolbar_icons/CursorToolbar.svg?react';
import LineMeasurmentToolbar from '../../assets/toolbar_icons/LineMeasurmentToolbar.svg?react';
import { CadTool } from './types';

interface CadLeftToolbarProps {
  tool: CadTool;
  onToolChange: (tool: CadTool) => void;
  onCheatSheetToggle: () => void;
  /** Whether scale has been calibrated */
  scaleSet: boolean;
  /** Whether an image is loaded (scale must be set before drawing when image is present) */
  hasImage: boolean;
}

export function CadLeftToolbar({ tool, onToolChange, onCheatSheetToggle, scaleSet, hasImage }: CadLeftToolbarProps) {
  const { t } = useTranslation();

  /** Drawing tools are disabled when an image is loaded but scale isn't set yet */
  const drawingDisabled = hasImage && !scaleSet;

  const btnCls = (active: boolean, disabled = false) =>
    `w-11 h-11 flex items-center justify-center rounded-lg transition-colors ${
      disabled
        ? 'opacity-30 cursor-not-allowed text-gray-500'
        : active
          ? 'bg-[#1a1a1a] text-[#d4ff33]'
          : 'bg-transparent text-white hover:bg-white/5 hover:text-gray-300'
    }`;

  const handleToolClick = (t: CadTool, disabled = false) => {
    if (disabled) return;
    onToolChange(t);
  };

  const disabledTooltip = t('projects.cadProject.setScaleFirst', 'Set scale first');

  return (
    <>
      {/* On a phone (drawing editor): below the header and above the bottom toolbar and list sheet. */}
      <div className="fixed left-[22px] top-[76px] md:top-1/2 md:-translate-y-1/2 max-h-[calc(100dvh-266px)] md:max-h-none overflow-y-auto md:overflow-visible flex flex-col items-center bg-[#1E1E1E] border border-white/10 rounded-xl p-1.5 w-[56px] shadow-2xl z-50">
        {/* Select — always available */}
        <Tooltip title={t('projects.cadProject.selectTool')} placement="right">
          <button onClick={() => onToolChange('select')} className={btnCls(tool === 'select')}>
            <CursorToolbar className="w-6 h-6" />
          </button>
        </Tooltip>

        <div className="w-8 h-[1px] bg-white/10 my-2" />

        {/* Draw */}
        <Tooltip title={drawingDisabled ? disabledTooltip : t('projects.cadProject.drawPolygon')} placement="right">
          <button
            onClick={() => handleToolClick('draw', drawingDisabled)}
            className={btnCls(tool === 'draw', drawingDisabled)}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" />
            </svg>
          </button>
        </Tooltip>

        {/* Circle */}
        <Tooltip title={drawingDisabled ? disabledTooltip : t('projects.cadProject.circleTool')} placement="right">
          <button
            onClick={() => handleToolClick('circle', drawingDisabled)}
            className={btnCls(tool === 'circle', drawingDisabled)}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
            </svg>
          </button>
        </Tooltip>

        {/* Roundabout (annulus / ring) */}
        <Tooltip title={drawingDisabled ? disabledTooltip : t('projects.cadProject.roundaboutTool')} placement="right">
          <button
            onClick={() => handleToolClick('roundabout', drawingDisabled)}
            className={btnCls(tool === 'roundabout', drawingDisabled)}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="12" cy="12" r="0.6" fill="currentColor" stroke="none" />
            </svg>
          </button>
        </Tooltip>

        <div className="w-8 h-[1px] bg-white/10 my-2" />

        {/* Subtract / Cut (polygon) */}
        <Tooltip title={drawingDisabled ? disabledTooltip : t('projects.cadProject.subtractTool')} placement="right">
          <button
            onClick={() => handleToolClick('subtract', drawingDisabled)}
            className={btnCls(tool === 'subtract', drawingDisabled)}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="12" height="12" rx="1" />
              <rect x="9" y="9" width="12" height="12" rx="1" strokeDasharray="3 2" />
              <line x1="13" y1="15" x2="17" y2="15" />
            </svg>
          </button>
        </Tooltip>

        {/* Subtract / Cut (circle) */}
        <Tooltip
          title={drawingDisabled ? disabledTooltip : t('projects.cadProject.subtractCircleTool')}
          placement="right"
        >
          <button
            onClick={() => handleToolClick('subtract-circle', drawingDisabled)}
            className={btnCls(tool === 'subtract-circle', drawingDisabled)}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="9" strokeDasharray="4 3" />
              <line x1="8" y1="12" x2="16" y2="12" />
            </svg>
          </button>
        </Tooltip>

        {/* Split */}
        <Tooltip
          title={drawingDisabled ? disabledTooltip : t('projects.cadProject.splitTool', 'Split polygon')}
          placement="right"
        >
          <button
            onClick={() => handleToolClick('split', drawingDisabled)}
            className={btnCls(tool === 'split', drawingDisabled)}
          >
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="12" y1="3" x2="12" y2="21" />
              <polyline points="8 8 4 12 8 16" />
              <polyline points="16 8 20 12 16 16" />
            </svg>
          </button>
        </Tooltip>

        <div className="w-8 h-[1px] bg-white/10 my-2" />

        {/* Measure — always available */}
        <Tooltip title={t('projects.cadProject.measureTool', 'Measure')} placement="right">
          <button onClick={() => onToolChange('measure')} className={btnCls(tool === 'measure')}>
            <LineMeasurmentToolbar className="w-6 h-6" />
          </button>
        </Tooltip>

        <div className="w-8 h-[1px] bg-white/10 my-2" />

        {/* Help */}
        <Tooltip title={t('projects.cadProject.cheatSheet.title')} placement="right">
          <button onClick={onCheatSheetToggle} className={btnCls(false)}>
            <svg
              className="w-6 h-6"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
          </button>
        </Tooltip>
      </div>
    </>
  );
}
